import { useEffect, useMemo, useState } from 'react';
import {
  getChargerFromBackend, updateChargerOnBackend,
  addPortOnBackend, deletePortOnBackend, updatePortOnBackend,
  getOrganizations, getVendors,
  type ChargerRow, type ChargerPortRow, type OrgOption, type VendorOption,
} from '../lib/api';
import { CONNECTOR_TYPES, DEFAULT_CONNECTOR } from '../lib/connectors';

interface Props {
  charger_id: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

// Local edit state for a port row. `_new: true` marks a port that hasn't been
// persisted yet, so submit() sends an addPort call instead of a PATCH.
interface EditPort {
  port_number:    number;
  connector_type: string;
  is_charging:    boolean;
  _new?:          boolean;
}

export default function EditChargerModal({ charger_id, onClose, onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // Base fields
  const [name,     setName]     = useState('');
  const [location, setLocation] = useState('');
  const [lat,      setLat]      = useState('');
  const [lng,      setLng]      = useState('');
  const [powerKw,  setPowerKw]  = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [orgId,    setOrgId]    = useState('');
  const [vendorId, setVendorId] = useState('');

  const [ports,    setPorts]    = useState<EditPort[]>([]);

  // Options for the dropdowns
  const [orgs,    setOrgs]    = useState<OrgOption[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);

  // Snapshot for diff-on-save.
  const [initial, setInitial] = useState<{
    name: string; location: string; lat: string; lng: string;
    powerKw: string; isPublic: boolean; orgId: string; vendorId: string;
    ports: ChargerPortRow[];
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [row, o, v] = await Promise.all([
          getChargerFromBackend(charger_id),
          getOrganizations(),
          getVendors(),
        ]) as [ChargerRow, OrgOption[], VendorOption[]];
        if (cancelled) return;

        const rowName    = row.name ?? '';
        const rowLoc     = row.location ?? '';
        const rowLat     = row.lat != null ? String(row.lat) : '';
        const rowLng     = row.lng != null ? String(row.lng) : '';
        const rowPower   = row.power_kw != null ? String(row.power_kw) : '';
        const rowPublic  = row.is_public !== false;
        const rowOrg     = row.org_id ?? '';
        const rowVendor  = row.vendor_id ?? '';
        const rowPorts   = (row.ports ?? []).slice().sort((a, b) => a.port_number - b.port_number);

        setName(rowName);
        setLocation(rowLoc);
        setLat(rowLat);
        setLng(rowLng);
        setPowerKw(rowPower);
        setIsPublic(rowPublic);
        setOrgId(rowOrg);
        setVendorId(rowVendor);
        setPorts(rowPorts.map((p) => ({
          port_number:    p.port_number,
          connector_type: p.connector_type || DEFAULT_CONNECTOR,
          is_charging:    p.is_charging,
        })));

        setOrgs(o);
        setVendors(v);
        setInitial({
          name: rowName, location: rowLoc, lat: rowLat, lng: rowLng,
          powerKw: rowPower, isPublic: rowPublic, orgId: rowOrg, vendorId: rowVendor,
          ports: rowPorts,
        });
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load charger');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [charger_id]);

  // Vendor list is filtered to those matching the currently-selected org.
  // Same rule the main backend enforces on PATCH — filter client-side so
  // the user can't pick an invalid combination.
  const filteredVendors = useMemo(() => {
    const orgFilter = orgId === '' ? null : orgId;
    return vendors.filter((v) => (v.org_id ?? null) === orgFilter);
  }, [vendors, orgId]);

  const nextFreePortNumber = (): number | null => {
    const taken = new Set(ports.map((p) => p.port_number));
    for (let i = 1; i <= 4; i++) if (!taken.has(i)) return i;
    return null;
  };

  const addPort = () => {
    const pn = nextFreePortNumber();
    if (pn == null) return;
    setPorts([...ports, { port_number: pn, connector_type: DEFAULT_CONNECTOR, is_charging: false, _new: true }]);
  };

  const removePort = (portNumber: number) => {
    const target = ports.find((p) => p.port_number === portNumber);
    if (!target) return;
    if (target.is_charging) { alert('Cannot remove a port that is currently charging. Stop the session first.'); return; }
    if (ports.length <= 1) { alert('A charger must have at least one port.'); return; }
    if (!confirm(`Remove Port ${portNumber}? This cannot be undone.`)) return;
    setPorts(ports.filter((p) => p.port_number !== portNumber));
  };

  const changePortConnector = (portNumber: number, next: string) => {
    setPorts(ports.map((p) => p.port_number === portNumber ? { ...p, connector_type: next } : p));
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!initial) return;
    setBusy(true);
    setError(null);
    try {
      // ── Base charger PATCH: only send fields that changed ────────────────
      const patch: Parameters<typeof updateChargerOnBackend>[1] = {};
      if (name     !== initial.name)     patch.name     = name.trim();
      if (location !== initial.location) patch.location = location.trim();
      if (lat !== initial.lat) {
        const n = Number(lat);
        if (!Number.isFinite(n)) throw new Error('Latitude must be a number');
        patch.lat = n;
      }
      if (lng !== initial.lng) {
        const n = Number(lng);
        if (!Number.isFinite(n)) throw new Error('Longitude must be a number');
        patch.lng = n;
      }
      if (powerKw !== initial.powerKw) {
        if (powerKw === '') { throw new Error('Max power is required'); }
        const n = Number(powerKw);
        if (!Number.isFinite(n) || n < 0) throw new Error('Max power must be a positive number');
        patch.power_kw = n;
      }
      if (isPublic !== initial.isPublic) patch.is_public = isPublic;
      if (orgId    !== initial.orgId)    patch.org_id    = orgId    === '' ? null : orgId;
      if (vendorId !== initial.vendorId) patch.vendor_id = vendorId === '' ? null : vendorId;

      if (Object.keys(patch).length > 0) {
        await updateChargerOnBackend(charger_id, patch);
      }

      // ── Ports diff: deletions → additions → connector-type PATCHes ───────
      const originalByNum = new Map(initial.ports.map((p) => [p.port_number, p]));
      const currentNums   = new Set(ports.map((p) => p.port_number));

      // Deletions first — frees the slot in case the user re-added a port
      // with the same number.
      for (const p of initial.ports) {
        if (!currentNums.has(p.port_number)) {
          await deletePortOnBackend(charger_id, p.port_number);
        }
      }

      for (const p of ports) {
        if (p._new) {
          await addPortOnBackend(charger_id, {
            port_number:    p.port_number,
            connector_type: p.connector_type,
          });
        } else {
          const orig = originalByNum.get(p.port_number);
          if (orig && (orig.connector_type || DEFAULT_CONNECTOR) !== p.connector_type) {
            await updatePortOnBackend(charger_id, p.port_number, { connector_type: p.connector_type });
          }
        }
      }

      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  const portCountChanged = initial != null &&
    (ports.length !== initial.ports.length
     || ports.some((p) => p._new)
     || initial.ports.some((op) => !ports.find((cp) => cp.port_number === op.port_number)));

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="modal" onSubmit={submit}>
        <h2>Edit {charger_id}</h2>
        {loading ? (
          <div className="muted">Loading…</div>
        ) : (
          <>
            <div className="field">
              <label>Display name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="field">
              <label>Address / location</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>

            <div className="row" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Latitude</label>
                <input value={lat} onChange={(e) => setLat(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Longitude</label>
                <input value={lng} onChange={(e) => setLng(e.target.value)} />
              </div>
            </div>

            <div className="row" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Organization</label>
                <select
                  value={orgId}
                  onChange={(e) => {
                    // Auto-clear vendor when org changes — the filtered list
                    // would otherwise hide the stale vendor and the backend
                    // would reject the cross-org combination.
                    const newOrg = e.target.value;
                    const orgFilter = newOrg === '' ? null : newOrg;
                    const current = vendors.find((v) => v.id === vendorId);
                    if (!current || (current.org_id ?? null) !== orgFilter) setVendorId('');
                    setOrgId(newOrg);
                  }}
                >
                  <option value="">— None —</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Vendor</label>
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                  <option value="">— None —</option>
                  {filteredVendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.business_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row" style={{ gap: 12 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>Max power (kW)</label>
                <input
                  type="number" step="0.1" min="0"
                  value={powerKw}
                  onChange={(e) => setPowerKw(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>Visibility</label>
                <select value={String(isPublic)} onChange={(e) => setIsPublic(e.target.value === 'true')}>
                  <option value="true">Public — visible to all customers</option>
                  <option value="false">Private — QR access only</option>
                </select>
              </div>
            </div>

            {/* Ports */}
            <div className="field">
              <label>Ports</label>
              <datalist id="connector-types-edit">
                {CONNECTOR_TYPES.map((c) => <option key={c} value={c} />)}
              </datalist>
              <div style={{ display: 'grid', gap: 6 }}>
                {ports
                  .slice()
                  .sort((a, b) => a.port_number - b.port_number)
                  .map((p) => (
                    <div key={p.port_number} className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <span className="muted" style={{ width: 70 }}>
                        Port {p.port_number}{p._new ? ' •' : ''}
                      </span>
                      <input
                        list="connector-types-edit"
                        value={p.connector_type}
                        onChange={(e) => changePortConnector(p.port_number, e.target.value)}
                        placeholder={DEFAULT_CONNECTOR}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => removePort(p.port_number)}
                        disabled={p.is_charging || ports.length <= 1}
                        title={
                          p.is_charging
                            ? 'Cannot remove a port that is currently charging'
                            : ports.length <= 1
                              ? 'A charger must have at least one port'
                              : 'Remove this port'
                        }
                        style={{ padding: '4px 10px', flex: '0 0 auto' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                <button
                  type="button"
                  className="ghost"
                  onClick={addPort}
                  disabled={ports.length >= 4}
                  style={{ padding: '4px 10px', alignSelf: 'flex-start' }}
                >
                  + Add port
                </button>
              </div>
              {portCountChanged && (
                <div className="muted mt" style={{ fontSize: 12 }}>
                  Port count changed — disconnect and reconnect this simulator to pick up the new count.
                </div>
              )}
            </div>

            <div className="row mt" style={{ gap: 8 }}>
              <button type="submit" disabled={busy} style={{ flex: 1 }}>{busy ? 'Saving…' : 'Save'}</button>
              <button type="button" className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </div>
          </>
        )}
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
